import React from 'react';
import { Upload, Loader2, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { isPaymentReceiptRequired } from '../../utils/paymentReceipt';

interface PaymentReceiptFieldProps {
  inputId: string;
  paymentReceipt: string;
  cashPayment: boolean;
  userRole: string | null;
  hideFinancialFields: boolean;
  canBypassRequiredFields: boolean;
  isUploading: boolean;
  onUpload: (files: FileList) => void;
  onDelete: () => void;
  onCashPaymentChange: (checked: boolean) => void;
  renderFileLinks: (url: string, field: string) => React.ReactNode;
}

const PaymentReceiptField: React.FC<PaymentReceiptFieldProps> = ({
  inputId,
  paymentReceipt,
  cashPayment,
  userRole,
  hideFinancialFields,
  canBypassRequiredFields,
  isUploading,
  onUpload,
  onDelete,
  onCashPaymentChange,
  renderFileLinks,
}) => {
  if (hideFinancialFields) return null;

  const required = isPaymentReceiptRequired({
    hideFinancialFields,
    canBypassRequiredFields,
    userRole,
    cashPayment,
    paymentReceipt,
  });

  const showCashOption = userRole === 'Vendedor';
  const uploadDisabled = isUploading || (showCashOption && cashPayment);

  return (
    <div>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        Comprobante de Pago{required ? ' *' : ''}
      </span>
      {showCashOption && (
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={cashPayment}
            onChange={(e) => onCashPaymentChange(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          Pago en efectivo (sin comprobante)
        </label>
      )}
      <div className="flex items-center gap-2 mt-1">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => {
            const f = e.target.files;
            if (f?.length) onUpload(f);
            e.target.value = '';
          }}
          className="hidden"
          id={inputId}
          disabled={uploadDisabled}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById(inputId)?.click()}
          disabled={uploadDisabled}
          icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          className="h-9 text-sm"
        >
          Subir Comprobante
        </Button>
        {paymentReceipt && (
          <>
            <div className="flex-1 min-w-0">{renderFileLinks(paymentReceipt, 'payment_receipt')}</div>
            <button type="button" onClick={onDelete} className="text-gray-400 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
      {required && !paymentReceipt && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">El comprobante de pago es obligatorio</p>
      )}
    </div>
  );
};

export default PaymentReceiptField;
